import { FC, ReactNode, useState } from 'react';
import { Button, Col, Divider, Row, Space, Switch, Tabs, Typography } from 'antd';
import {
    TbCalendar,
    TbCalendarMonth,
    TbCalendarRepeat,
    TbCalendarStats,
    TbCalendarTime,
    TbCalendarWeek,
    TbCircle,
    TbMail,
    TbMessageCircle,
    TbMinusVertical,
    TbSettings,
} from 'react-icons/tb';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { SlackSVGIcon } from '@/assets';
import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useEntitlements } from '@/hooks/useEntitlements';
import {
    AlertIcon,
    FlexContentRegion,
    FullHeightFlexColumn,
    LearnMoreFooter,
    PlanTag,
    ScheduleDropdownOption,
    ScheduleOptionDropdown,
} from '@/shared';
import InlineIconLabel from '@/shared/components/modals/ResourceCreationModals/CreateOAuthApplicationModal/components/InlineIconLabel';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { ACCOUNT_NOTIFICATION_SETTINGS_PATHS } from '@/shared/constants';
import { useUserStore } from '@/stores';
import { useUserPreferences } from '@/stores/database/entities/00-RBAC/99-UserPreferences';

import CustomDigestSchedule, { DigestCustomScheduleValue } from './components/CustomDigestSchedule';
import NotificationGroup, { NotificationChannel, NotificationChannelConfig } from './components/NotificationGroup';
import { getNotificationChannelGridColumns } from './components/NotificationGroup/layout';
import SlackNotificationModal from './components/SlackNotificationModal';
import Metadata from './meta';
import { isNotificationChannelEnabled } from './notificationChannelPolicy';
import { buildNotificationGroups, notificationWorkflowDefaultEnabled } from './notificationGroups';
import { resolveNotificationSettingsLocation } from './notificationRoutes';
import AlertsView from './views/02-Alerts';

type DigestFrequency = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'semiAnnual' | 'annual' | 'custom';

const digestFrequencyOptions: Array<ScheduleDropdownOption<DigestFrequency>> = [
    { value: 'daily', label: 'Daily', description: 'Receive a digest every day.', icon: <TbCalendar /> },
    { value: 'weekly', label: 'Weekly', description: 'Receive a digest once a week.', icon: <TbCalendarWeek /> },
    { value: 'monthly', label: 'Monthly', description: 'Receive a digest once a month.', icon: <TbCalendarMonth /> },
    {
        value: 'quarterly',
        label: 'Quarterly',
        description: 'Receive a digest every quarter.',
        icon: <TbCalendarStats />,
    },
    {
        value: 'semiAnnual',
        label: 'Semi-Annual',
        description: 'Receive a digest twice a year.',
        icon: <TbCalendarRepeat />,
    },
    { value: 'annual', label: 'Annual', description: 'Receive a digest once a year.', icon: <TbCalendar /> },
    {
        value: 'custom',
        label: 'Custom',
        description: 'Choose delivery days, time, and timezone.',
        dividerBefore: true,
        icon: <TbCalendarTime />,
    },
];

const channelDefinitions = [
    {
        key: 'email',
        label: 'Email',
        description: 'Send inbox activity, workflow updates, and digest summaries to your email address.',
        icon: <TbMail size='1rem' style={{ verticalAlign: '-0.15rem', marginRight: '0.5rem' }} />,
    },
    {
        key: 'inApp',
        label: 'In-App',
        description: 'Show updates in the platform inbox and notification preview while you work.',
        icon: <TbMessageCircle size='1rem' style={{ verticalAlign: '-0.15rem', marginRight: '0.5rem' }} />,
    },
    {
        key: 'push',
        label: 'Push',
        description: 'Deliver alerts to supported clients when push is available.',
        icon: <AlertIcon style={{ marginRight: '0.5rem', verticalAlign: '-0.15rem' }} />,
    },
    {
        key: 'slack',
        label: 'Slack',
        description: 'Send selected workspace and resource notifications to the Slack channels you configure.',
        icon: (
            <img
                alt=''
                aria-hidden
                src={SlackSVGIcon}
                style={{ height: '1rem', marginRight: '0.5rem', verticalAlign: '-0.15rem', width: '1rem' }}
            />
        ),
    },
] satisfies NotificationChannelConfig[];

export const GeneralNotifications: FC = (): JSX.Element => {
    const [slackModalOpen, setSlackModalOpen] = useState(false);
    const { selectedUser: user } = useUserStore();
    const { preferences, setPreference } = useUserPreferences();
    const { token } = useAntDesignTheme();
    const { hasMinimumPlan } = useEntitlements();
    const digestAvailable = hasMinimumPlan('pro');
    const digestEnabled = preferences?.inbox?.digest?.enabled ?? false;
    const digestControlEnabled = digestAvailable && digestEnabled;
    const digestFrequency = (preferences?.inbox?.digest?.frequency ?? 'weekly') as DigestFrequency;
    const digestCustomSchedule: DigestCustomScheduleValue = {
        timeOfDay: preferences?.inbox?.digest?.customSchedule?.timeOfDay ?? '08:00',
        timezone:
            preferences?.inbox?.digest?.customSchedule?.timezone ??
            Intl.DateTimeFormat().resolvedOptions().timeZone ??
            'America/Chicago',
        weekDays: preferences?.inbox?.digest?.customSchedule?.weekDays ?? [1, 2, 3, 4, 5],
    };
    const groups = buildNotificationGroups(user?.firstName);
    const workflowDefaults = notificationWorkflowDefaultEnabled(groups);
    const slackChannelIds = preferences?.notifications?.slack?.channelIds ?? [];
    const slackConnectedAccountId = preferences?.notifications?.slack?.connectedAccountId;

    const isGlobalChannelEnabled = (channel: NotificationChannel) =>
        isNotificationChannelEnabled({ channel, preferences });

    const isWorkflowChannelEnabled = (workflowKey: string, channel: NotificationChannel) =>
        isNotificationChannelEnabled({
            channel,
            preferences,
            workflowDefaultEnabled: workflowDefaults[workflowKey],
            workflowKey,
        });

    const setGlobalChannel = (channel: NotificationChannel, checked: boolean) => {
        if (channel === 'slack' && checked && slackChannelIds.length === 0) {
            setSlackModalOpen(true);

            return;
        }

        setPreference(`notifications.channels.${channel}`, checked);
    };

    const setWorkflowChannel = (workflowKey: string, channel: NotificationChannel, checked: boolean) => {
        setPreference(`notifications.workflows.${workflowKey}.${channel}`, checked);
    };

    const activeChannelDefinitions = channelDefinitions.filter((channel) => isGlobalChannelEnabled(channel.key));

    const notificationGridStyle = {
        alignItems: 'center',
        columnGap: 0,
        display: 'grid',
        gridTemplateColumns: getNotificationChannelGridColumns(activeChannelDefinitions.length),
        width: '100%',
    } as const;

    const renderChannelHeader = (channel: NotificationChannelConfig) => (
        <div key={channel.key} style={{ minWidth: 0, textAlign: 'center' }}>
            <span style={{ color: token.colorTextTertiary }}>{channel.label}</span>
        </div>
    );

    return (
        <FullHeightFlexColumn data-notifications-general-layout='true'>
            <FlexContentRegion data-notifications-general-scroll-region='true' overflowX='hidden' overflowY='auto'>
                <div data-notifications-general-content='true' style={{ padding: '1rem 0 0' }}>
                    <Typography.Title
                        id='settings-account-notifications-methods'
                        level={2}
                        style={{ scrollMarginTop: '0.75rem' }}
                    >
                        Methods
                    </Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        Choose where notifications can reach you. Disabled methods are removed from the controls below.
                    </Typography.Paragraph>
                    {channelDefinitions.map((channel) => (
                        <Row
                            key={channel.key}
                            id={`settings-account-notifications-method-${channel.key}`}
                            align='middle'
                            gutter={[16, 16]}
                            wrap={false}
                            style={{ marginBottom: '0.75rem', scrollMarginTop: '0.75rem' }}
                        >
                            <Col flex='auto' style={{ minWidth: 0 }}>
                                <Typography.Title level={5} style={{ marginBottom: '0.125rem' }}>
                                    {channel.icon as ReactNode}
                                    {channel.label}
                                </Typography.Title>
                                <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: 0 }}>
                                    {channel.description}
                                </Typography.Paragraph>
                            </Col>
                            <Col flex='none' style={{ textAlign: 'right' }}>
                                <Space size={8}>
                                    {channel.key === 'slack' && (
                                        <Button size='small' type='dashed' onClick={() => setSlackModalOpen(true)}>
                                            <TbSettings />
                                            Configure
                                        </Button>
                                    )}
                                    <Switch
                                        checked={isGlobalChannelEnabled(channel.key)}
                                        onChange={(checked) => setGlobalChannel(channel.key, checked)}
                                        checkedChildren={<TbMinusVertical style={{ verticalAlign: '-0.125rem' }} />}
                                        unCheckedChildren={<TbCircle style={{ verticalAlign: '-0.125rem' }} />}
                                    />
                                </Space>
                            </Col>
                        </Row>
                    ))}
                    <Divider style={{ margin: '1rem 0' }} />
                    <Typography.Title
                        id='settings-account-notifications-digests'
                        level={2}
                        style={{ scrollMarginTop: '0.75rem' }}
                    >
                        Digests
                    </Typography.Title>
                    <Typography.Paragraph style={{ color: token.colorTextTertiary }}>
                        Bundle subscribed workspace activity into a scheduled summary that respects your preferred
                        timezone.
                    </Typography.Paragraph>
                    <section
                        id='settings-account-notifications-digest-workspace'
                        style={{
                            border: `1px dashed ${token.colorBorder}`,
                            borderRadius: token.borderRadius,
                            padding: '0.75rem',
                            scrollMarginTop: '0.75rem',
                        }}
                    >
                        <Row align='middle' gutter={[16, 16]} wrap={false}>
                            <Col flex='auto' style={{ minWidth: 0 }}>
                                <Typography.Title level={5} style={{ marginBottom: '0.125rem' }}>
                                    <TbCalendarStats
                                        size='1rem'
                                        style={{ verticalAlign: '-0.15rem', marginRight: '0.5rem' }}
                                    />
                                    Workspace Digest
                                    <PlanTag plan='pro' style={{ marginLeft: '0.5rem' }} />
                                </Typography.Title>
                                <Typography.Paragraph style={{ color: token.colorTextTertiary, marginBottom: 0 }}>
                                    Receive a personalized feed of subscribed workspace activity.
                                </Typography.Paragraph>
                            </Col>
                            <Col flex='none' style={{ width: '18rem' }}>
                                <ScheduleOptionDropdown
                                    disabled={!digestControlEnabled}
                                    footer={<LearnMoreFooter topic='Workspace Digests' variant='dropdown' />}
                                    maxHeight='none'
                                    menuWidth='18rem'
                                    value={digestFrequency}
                                    options={digestFrequencyOptions}
                                    onChange={(value) => {
                                        if (!digestAvailable) {
                                            return;
                                        }

                                        setPreference('inbox.digest.frequency', value);

                                        if (value === 'custom' && !preferences?.inbox?.digest?.customSchedule) {
                                            setPreference('inbox.digest.customSchedule', digestCustomSchedule);
                                        }
                                    }}
                                />
                            </Col>
                            <Col flex='none'>
                                <Switch
                                    checked={digestControlEnabled}
                                    disabled={!digestAvailable}
                                    onChange={(checked) => {
                                        if (!digestAvailable) {
                                            return;
                                        }

                                        setPreference('inbox.digest.enabled', checked);

                                        if (checked && !preferences?.inbox?.digest?.frequency) {
                                            setPreference('inbox.digest.frequency', 'weekly');
                                        }
                                    }}
                                    checkedChildren={<TbMinusVertical style={{ verticalAlign: '-0.125rem' }} />}
                                    unCheckedChildren={<TbCircle style={{ verticalAlign: '-0.125rem' }} />}
                                />
                            </Col>
                        </Row>
                        {digestAvailable && digestFrequency === 'custom' && (
                            <CustomDigestSchedule
                                disabled={!digestControlEnabled}
                                value={digestCustomSchedule}
                                onChange={(value) => setPreference('inbox.digest.customSchedule', value)}
                            />
                        )}
                    </section>
                    <Divider style={{ margin: '1.5rem 0' }} />
                    <Typography.Title
                        id='settings-account-notifications-general'
                        level={2}
                        style={{ scrollMarginTop: '0.75rem' }}
                    >
                        Channel Preferences
                    </Typography.Title>
                    <div style={notificationGridStyle}>
                        <div style={{ minWidth: 0 }}>
                            <span style={{ color: token.colorTextTertiary }}>Notify me about...</span>
                        </div>
                        {activeChannelDefinitions.map(renderChannelHeader)}
                    </div>
                    {activeChannelDefinitions.length === 0 && (
                        <Typography.Paragraph style={{ color: token.colorTextTertiary, margin: '0.5rem 0 0' }}>
                            Enable at least one notification method to configure delivery by resource.
                        </Typography.Paragraph>
                    )}
                    <Divider style={{ marginTop: '1rem', marginBottom: '0.5rem' }} />
                    {groups.map((group) => (
                        <NotificationGroup
                            key={group.key}
                            channels={activeChannelDefinitions}
                            group={group}
                            isChannelEnabled={isWorkflowChannelEnabled}
                            onChannelChange={setWorkflowChannel}
                        />
                    ))}
                </div>
            </FlexContentRegion>
            <footer
                aria-label='Notification preferences documentation'
                data-notifications-general-footer='true'
                style={{ flex: 'none', marginTop: 'auto', paddingBottom: '1rem' }}
            >
                <Divider data-notifications-general-footer-divider='true' style={{ margin: '0 0 1rem' }} />
                <LearnMoreFooter topic='how we handle notifications' />
            </footer>
            <SlackNotificationModal
                connectedAccountId={slackConnectedAccountId}
                open={slackModalOpen}
                selectedChannelIds={slackChannelIds}
                setOpen={setSlackModalOpen}
                onSave={(value) => {
                    setPreference('notifications.slack', value);
                    setPreference('notifications.channels.slack', true);
                    toast.success('Slack notification channels updated.');
                }}
            />
        </FullHeightFlexColumn>
    );
};

const Notifications: FC = (): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { hasFeature } = useEntitlements();
    const location = useLocation();
    const navigate = useNavigate();
    const canUseAlerts = hasFeature('alerts');
    const canonicalLocation = resolveNotificationSettingsLocation(location);
    const needsCanonicalRedirect =
        location.pathname !== canonicalLocation.pathname ||
        location.search !== canonicalLocation.search ||
        location.hash !== canonicalLocation.hash;

    if (needsCanonicalRedirect) {
        return (
            <Navigate
                replace
                to={{
                    hash: canonicalLocation.hash,
                    pathname: canonicalLocation.pathname,
                    search: canonicalLocation.search,
                }}
            />
        );
    }

    if (!canUseAlerts && canonicalLocation.section === 'alerts') {
        return <Navigate replace to={ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general} />;
    }

    return (
        <FullHeightFlexColumn data-notifications-layout='true'>
            <Metadata />
            <Typography.Title style={{ flex: 'none' }}>Notifications</Typography.Title>
            <Typography.Paragraph style={{ flex: 'none' }}>
                <span style={{ color: token.colorTextTertiary }}>
                    Manage notification preferences and permission-aware workspace alerts.
                </span>
                <PlanTag plan='enterprise' style={{ marginLeft: '0.5rem' }} />
            </Typography.Paragraph>
            <Tabs
                activeKey={canonicalLocation.section}
                size='small'
                items={[
                    {
                        key: 'general',
                        label: <InlineIconLabel icon={<TbSettings size='1rem' />} label='General' />,
                    },
                    {
                        disabled: !canUseAlerts,
                        key: 'alerts',
                        label: <InlineIconLabel icon={<AlertIcon size='1rem' />} label='Alerts' />,
                    },
                ]}
                style={{ flex: 'none' }}
                tabBarStyle={{ marginBottom: 0 }}
                onChange={(section) =>
                    navigate(
                        section === 'alerts'
                            ? ACCOUNT_NOTIFICATION_SETTINGS_PATHS.alerts
                            : ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general,
                    )
                }
            />
            <FlexContentRegion
                data-notifications-route-content='true'
                overflowX='hidden'
                overflowY={canonicalLocation.section === 'general' ? 'hidden' : 'auto'}
            >
                <Routes>
                    <Route path='general' element={<GeneralNotifications />} />
                    <Route
                        path='alerts/*'
                        element={
                            canUseAlerts ? (
                                <AlertsView />
                            ) : (
                                <Navigate replace to={ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general} />
                            )
                        }
                    />
                    <Route path='*' element={<Navigate to={ACCOUNT_NOTIFICATION_SETTINGS_PATHS.general} replace />} />
                </Routes>
            </FlexContentRegion>
        </FullHeightFlexColumn>
    );
};

export default Notifications;
